import React, { useMemo } from 'react'
import _ from 'lodash'
import moment from 'moment'
import numeral from 'numeral'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import {
  transactionsConfig,
  lineItemsConfig,
  productsConfig,
  productTrendsConfig,
  variantsConfig,
  brandsConfig,
  skusConfig,
  customersConfig,
  customOrderBasesConfig,
  sortReportColKeys,
  utmsConfig,
  orderTransactionsConfig,
  yearComparisonMetricMappings,
  getYearComparisonMetricValue,
  trafficSessionsConfig,
  trafficAdsConfig,
  trafficKlaviyoConfig,
  getTrafficConfig,
  isTrafficSearchType,
  isTrafficAdsSearchType,
  inventoryProductsConfig,
  inventoryVariantsConfig,
  inventoryBrandsConfig,
  isInventorySearchType,
} from '../../../modules/formatting'
import { initialColumnConfigs } from '../../../modules/reportConfigs'
import { pivotColumnTitle } from '../pivotFields'

const configSets = {
  ordersConfig: transactionsConfig,
  lineItemsConfig,
  productsConfig,
  productTrendsConfig,
  variantsConfig,
  brandsConfig,
  skusConfig,
  customersConfig,
  customOrderBasesConfig,
  ...Object.keys(utmsConfig).reduce(
    (acc, utmKey) => ({
      ...acc,
      [`${utmKey}Config`]: utmsConfig[utmKey],
    }),
    {}
  ),
  orderTransactionsConfig,
  // Traffic report configs
  trafficSessionsConfig,
  trafficVisitorsConfig: trafficSessionsConfig,
  trafficCartsConfig: trafficSessionsConfig,
  trafficCheckoutsConfig: trafficSessionsConfig,
  trafficOrdersConfig: trafficSessionsConfig,
  trafficGoogleAdsConfig: trafficAdsConfig,
  trafficFacebookAdsConfig: trafficAdsConfig,
  trafficKlaviyoConfig,
  trafficPinterestConfig: trafficAdsConfig,
  trafficTiktokConfig: trafficAdsConfig,
  trafficAmazonAdsConfig: trafficAdsConfig,
  // Inventory report configs
  inventoryProductsConfig,
  inventoryVariantsConfig,
  inventoryBrandsConfig,
}

// Traffic report types that use docs array
const trafficBasicReportTypes = [
  'trafficSessions',
  'trafficVisitors',
  'trafficCarts',
  'trafficCheckouts',
  'trafficOrders',
]

// Traffic ads report types that display as metric cards
const trafficAdsReportTypes = [
  'trafficGoogleAds',
  'trafficFacebookAds',
  'trafficKlaviyo',
  'trafficPinterest',
  'trafficTiktok',
  'trafficAmazonAds',
]

// Inventory report types
const inventoryReportTypes = [
  'inventoryProducts',
  'inventoryVariants',
  'inventoryBrands',
]

// Render ProductTrends table with special structure
const renderProductTrendsTable = (data, reportData, maxRows) => {
  if (!data.length) return null

  const limitedData = data.slice(0, Math.floor(maxRows / 3)) // Account for multiple rows per product
  const { titles, formatters } = productTrendsConfig
  const appliedProductFields = _.get(reportData, ['config', 'params', 'product_info_fields'], [])
  const appliedFields = _.get(reportData, ['config', 'params', 'fields']) || []
  const appliedFieldValues = _.get(reportData, ['config', 'params', 'field_values']) || []
  const fieldValue = appliedFieldValues[0]
  const field = appliedFields[0]

  if (!limitedData[0] || !limitedData[0].trends) return null

  const dates = limitedData[0].trends.map(t => t.val)
  const keys = ['sales', 'items_sold', 'customers'] // Common metrics

  const dataRows = []
  limitedData.forEach((el, index) => {
    keys.forEach((key, keyIndex) => {
      dataRows.push(
        <tr key={`data-row-${index}-${key}`} className="hover:tw-bg-gray-50">
          {keyIndex === 0 && (
            <td rowSpan={keys.length} className="tw-px-4 tw-py-3 tw-text-sm tw-font-medium tw-text-blue-600 tw-border-r tw-border-gray-200">
              {el.value || 'N/A'}
            </td>
          )}
          {keyIndex === 0 && appliedProductFields.map(field => (
            <td key={field} rowSpan={keys.length} className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900 tw-border-r tw-border-gray-200">
              {field === 'image_url' ? (
                <img src={el[field]} alt="Product" className="tw-w-12 tw-h-12 tw-object-cover tw-rounded" />
              ) : (
                el[field] || 'N/A'
              )}
            </td>
          ))}
          <td className="tw-px-4 tw-py-3 tw-text-sm tw-font-medium tw-text-gray-700 tw-border-r tw-border-gray-200">
            {titles[key] || key}
          </td>
          {el.trends.map((trendEntry, trendIndex) => (
            <td key={`data-cell-${index}-${key}-${trendIndex}`} className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900 tw-text-right">
              {formatters[key] ? formatters[key](trendEntry[key], 'USD') : (trendEntry[key] || '-')}
            </td>
          ))}
        </tr>
      )
    })
  })

  return (
    <div className="tw-mt-4 tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm">
      <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
        <thead className="tw-bg-gray-50">
          <tr>
            <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
              {fieldValue ? `${fieldValue} (${titles[field] || field})` : titles[field] || field}
            </th>
            {appliedProductFields.map(field => (
              <th key={field} className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                {titles[field] || field}
              </th>
            ))}
            <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
              Metric
            </th>
            {dates.map(d => (
              <th key={d} className="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
          {dataRows}
        </tbody>
      </table>
      {data.length > Math.floor(maxRows / 3) && (
        <div className="tw-px-4 tw-py-3 tw-bg-gray-50 tw-text-sm tw-text-gray-500 tw-text-center">
          Showing {Math.floor(maxRows / 3)} of {data.length} products
        </div>
      )}
    </div>
  )
}

// Render YearComparison table
const renderYearComparisonTable = (reportData, maxRows) => {
  // Get yearComparisons from reportData
  const yearComparisons = reportData?.data?.yearComparisons || {}
  const trendKeysParam = _.get(reportData, ['config', 'params', 'trend_keys'], [])
  // Get available years and sort them
  const availableYears = Object.keys(yearComparisons).sort()

  // If no years available, return null
  if (availableYears.length === 0) {
    return null
  }

  // If no trend_keys configured, use default metrics
  const defaultMetrics = ['sales', 'items_sold', 'total_orders', 'customers', 'profit', 'gross_margin']
  const metricsToShow = trendKeysParam.length > 0 ? trendKeysParam : defaultMetrics

  // Group data by metric and period
  const tableRows = []
  const groupedData = {}
  const periodMap = new Map()

  // First pass: identify all unique periods by their start/end dates
  Object.keys(yearComparisons).forEach((year) => {
    const yearData = yearComparisons[year]
    if (yearData && yearData.trends) {
      yearData.trends.forEach((trend) => {
        const startDate = new Date(trend.period_start)
        const endDate = new Date(trend.period_end)
        const startKey = `${startDate.getMonth() + 1}-${startDate.getDate()}`
        const endKey = `${endDate.getMonth() + 1}-${endDate.getDate()}`
        const periodKey = `${startKey}_${endKey}`

        if (!periodMap.has(periodKey)) {
          periodMap.set(periodKey, {
            periodStart: trend.period_start,
            periodEnd: trend.period_end,
            dates: [],
          })
        }

        periodMap.get(periodKey).dates.push({
          year: year,
          date: trend.date,
          trend: trend,
        })
      })
    }
  })

  // Second pass: create grouped data using the period map
  metricsToShow.forEach((metricKey) => {
    // Define proper formatting for common metrics
    const defaultMetricConfig = {
      title: metricKey,
      isCurrency: ['sales', 'profit', 'avg_price_per_item', 'tax', 'shipping', 'discount', 'net_sales', 'gross_sales', 'cost_of_goods_sold'].includes(metricKey),
      isPercentage: ['gross_margin'].includes(metricKey),
      isCount: ['items_sold', 'total_orders', 'customers', 'count'].includes(metricKey)
    }

    const metricConfig = yearComparisonMetricMappings[metricKey] || defaultMetricConfig

    periodMap.forEach((periodInfo, periodKey) => {
      const rowKey = `${periodKey}-${metricKey}`
      if (!groupedData[rowKey]) {
        groupedData[rowKey] = {
          metric: metricConfig.title,
          periodKey: periodKey,
          periodStart: periodInfo.periodStart,
          periodEnd: periodInfo.periodEnd,
          yearValues: {},
          isCurrency: metricConfig.isCurrency,
          isPercentage: metricConfig.isPercentage,
        }
      }

      // Collect data for each year for this period/metric
      periodInfo.dates.forEach((dateInfo) => {
        const value = getYearComparisonMetricValue(dateInfo.trend, metricKey)
        groupedData[rowKey].yearValues[dateInfo.year] = value
      })
    })
  })

  Object.values(groupedData).forEach((rowData) => {
    if (Object.keys(rowData.yearValues).length > 0) {
      tableRows.push(rowData)
    }
  })

  const limitedRows = tableRows.slice(0, maxRows)

  return (
    <div className="tw-mt-4 tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm">
      <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
        <thead className="tw-bg-gray-50">
          <tr>
            <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
              Metric
            </th>
            <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
              Period Start
            </th>
            <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
              Period End
            </th>
            {availableYears.map((year) => (
              <th key={year} className="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                {year}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
          {limitedRows.map((row, index) => (
            <tr key={`${row.metric}-${row.periodKey}-${index}`} className="hover:tw-bg-gray-50">
              <td className="tw-px-4 tw-py-3 tw-text-sm tw-font-medium tw-text-gray-900">{row.metric}</td>
              <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900">
                {row.periodStart ? moment.utc(row.periodStart).format('YYYY-MM-DD') : '-'}
              </td>
              <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900">
                {row.periodEnd ? moment.utc(row.periodEnd).format('YYYY-MM-DD') : '-'}
              </td>
              {availableYears.map((year) => {
                const value = row.yearValues[year]
                let formattedValue = '-'
                if (value !== undefined && value !== null) {
                  formattedValue = row.isCurrency
                    ? numeral(value).format('$0,0')
                    : numeral(value).format('0,0')

                  formattedValue = row.isPercentage
                    ? numeral(value).format('0.0%')
                    : formattedValue
                }
                return (
                  <td key={year} className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900 tw-text-right">
                    {formattedValue}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {tableRows.length > maxRows && (
        <div className="tw-px-4 tw-py-3 tw-bg-gray-50 tw-text-sm tw-text-gray-500 tw-text-center">
          Showing {maxRows} of {tableRows.length} rows
        </div>
      )}
    </div>
  )
}

// Map trend_keys to actual data keys (for API data access)
const metricKeyMap = {
  orders: 'count',
  total_orders: 'count',
}

// Format metric value for display
const formatMetricValue = (value, isCurrency) => {
  if (value === null || value === undefined) return '-'
  if (value === 0) return '-'
  if (isCurrency) {
    return numeral(value).format('$0,0.00')
  }
  return numeral(value).format('0,0')
}

// Render grouped trends pivot table
const renderGroupedTrendsTable = (trendsData, groupByField, tableMetrics, maxRows) => {
  const groupedFieldKey = `by_${groupByField}`

  // Get field title for display
  const fieldTitle = groupByField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())

  // Define all available metrics with their display config
  const allMetricConfigs = {
    sales: { title: 'Sales', isCurrency: true },
    count: { title: 'Orders', isCurrency: false },
    orders: { title: 'Orders', isCurrency: false, dataKey: 'count' },
    items_sold: { title: 'Items Sold', isCurrency: false },
    customers: { title: 'Customers', isCurrency: false },
    avg_price_per_item: { title: 'Avg. Price', isCurrency: true },
    shipping: { title: 'Shipping', isCurrency: true },
    cost_of_goods_sold: { title: 'COGS', isCurrency: true },
    profit: { title: 'Profit', isCurrency: true },
    tax: { title: 'Tax', isCurrency: true },
    marketplace_fees: { title: 'Marketplace Fees', isCurrency: true },
    net_sales: { title: 'Net Sales', isCurrency: true },
    gross_sales: { title: 'Gross Sales', isCurrency: true },
    discount: { title: 'Discount', isCurrency: true },
  }

  // Build flat data rows: one row per date + field value combination
  // Track dateIndex for alternating colors
  const flatData: any[] = []
  trendsData.forEach((trend, trendIdx) => {
    const groupedData = trend[groupedFieldKey] || {}
    const sortedFieldValues = Object.keys(groupedData).sort()
    sortedFieldValues.forEach((fieldValue, fvIdx) => {
      flatData.push({
        date: trend.date,
        fieldValue: fieldValue,
        data: groupedData[fieldValue] || {},
        dateIndex: trendIdx,
        isFirstInDate: fvIdx === 0
      })
    })
  })

  if (flatData.length === 0) {
    return <div className="tw-text-gray-500 tw-p-4">No grouped data to display</div>
  }

  const limitedData = flatData.slice(0, maxRows)

  // Determine which metrics to show based on what's available in the data
  const metricsToShow = Object.keys(allMetricConfigs).filter(metricKey => {
    const dataKey = allMetricConfigs[metricKey].dataKey || metricKey
    return flatData.some(row => row.data[dataKey] !== undefined && row.data[dataKey] !== null)
  })

  // Format value for display
  const formatValue = (value, isCurrency) => {
    if (value === null || value === undefined) return '$0.00'
    if (isCurrency) {
      return value < 0 ? `-$${numeral(Math.abs(value)).format('0,0.00')}` : `$${numeral(value).format('0,0.00')}`
    }
    return numeral(value).format('0,0')
  }

  return (
    <div className="tw-mt-4">
      <div className="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm">
        <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
          <thead className="tw-bg-gray-50">
            <tr>
              <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                Date
              </th>
              <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                {fieldTitle}
              </th>
              {metricsToShow.map(metricKey => (
                <th key={metricKey} className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                  {allMetricConfigs[metricKey].title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
            {limitedData.map((row, index) => (
              <tr key={index} className="hover:tw-bg-gray-50">
                <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900 tw-whitespace-nowrap" style={{ fontWeight: row.isFirstInDate ? '500' : '400' }}>
                  {row.isFirstInDate ? moment(row.date).format('YYYY-MM-DD') : ''}
                </td>
                <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900 tw-whitespace-nowrap">
                  {row.fieldValue}
                </td>
                {metricsToShow.map(metricKey => {
                  const config = allMetricConfigs[metricKey]
                  const dataKey = config.dataKey || metricKey
                  const value = row.data[dataKey]
                  return (
                    <td key={metricKey} className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900 tw-whitespace-nowrap">
                      {formatValue(value, config.isCurrency)}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {flatData.length > maxRows && (
          <div className="tw-px-4 tw-py-3 tw-bg-gray-50 tw-text-sm tw-text-gray-500 tw-text-center">
            Showing {maxRows} of {flatData.length} results
          </div>
        )}
      </div>
    </div>
  )
}

// Render regular Trends table
const renderTrendsTable = (trendsData, maxRows, reportData = null) => {
  if (!trendsData || !trendsData.length) return null

  // Check if there's a trendGroupByField for grouped trends display
  let trendGroupByField = _.get(reportData, ['config', 'params', 'trendGroupByField'])
  const trendKeysConfig = _.get(reportData, ['config', 'params', 'trend_keys'], ['sales', 'orders'])

  // Auto-detect grouped data by looking for by_* keys in the trends data
  if (!trendGroupByField && Array.isArray(trendsData) && trendsData[0]) {
    const firstTrend = trendsData[0]
    const byKeys = Object.keys(firstTrend).filter(key => key.startsWith('by_'))
    if (byKeys.length > 0) {
      // Extract the field name from the first by_* key (e.g., 'by_financial_status' -> 'financial_status')
      trendGroupByField = byKeys[0].replace('by_', '')
    }
  }

  // Handle grouped trends data
  if (trendGroupByField && Array.isArray(trendsData) && trendsData[0]) {
    const groupedFieldKey = `by_${trendGroupByField}`
    const hasGroupedData = trendsData.some(trend => trend[groupedFieldKey] && Object.keys(trend[groupedFieldKey]).length > 0)

    if (hasGroupedData) {
      return renderGroupedTrendsTable(trendsData, trendGroupByField, trendKeysConfig, maxRows)
    }
  }

  // Handle direct array format (like the provided trends data)
  if (Array.isArray(trendsData) && trendsData[0] && !trendsData[0].keys) {
    const limitedData = trendsData.slice(0, maxRows)

    // Define key metrics to display for trends data
    const trendKeys = [
      'date',
      'sales',
      'items_sold',
      'total_orders',
      'customers',
      'avg_price_per_item',
      'profit',
      'gross_margin'
    ]

    // Filter keys to only show ones that exist in the data
    const availableKeys = trendKeys.filter(key =>
      limitedData.some(row => row.hasOwnProperty(key) && row[key] !== null && row[key] !== undefined)
    )

    // Define titles for the columns
    const columnTitles = {
      date: 'Date',
      sales: 'Sales',
      items_sold: 'Items Sold',
      total_orders: 'Total Orders',
      customers: 'Customers',
      avg_price_per_item: 'Avg Price/Item',
      profit: 'Profit',
      gross_margin: 'Gross Margin',
      tax: 'Tax',
      shipping: 'Shipping',
      discount: 'Discount',
      net_sales: 'Net Sales',
      gross_sales: 'Gross Sales',
      cost_of_goods_sold: 'COGS'
    }

    // Define formatters for different data types
    const formatValue = (value, key) => {
      if (value === null || value === undefined) return '-'

      switch (key) {
        case 'date':
          return moment(value).format('MMM DD, YYYY')
        case 'sales':
        case 'profit':
        case 'avg_price_per_item':
        case 'tax':
        case 'shipping':
        case 'discount':
        case 'net_sales':
        case 'gross_sales':
        case 'cost_of_goods_sold':
          return numeral(value).format('$0,0.00')
        case 'gross_margin':
          return numeral(value).format('0.0%')
        case 'items_sold':
        case 'total_orders':
        case 'customers':
          return numeral(value).format('0,0')
        default:
          return value.toString()
      }
    }

    return (
      <div className="tw-mt-4 tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm">
        <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
          <thead className="tw-bg-gray-50">
            <tr>
              {availableKeys.map((key) => (
                <th key={key} className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                  {columnTitles[key] || key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
            {limitedData.map((row, index) => (
              <tr key={index} className="hover:tw-bg-gray-50">
                {availableKeys.map((key) => (
                  <td key={key} className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900 tw-whitespace-nowrap">
                    {formatValue(row[key], key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {trendsData.length > maxRows && (
          <div className="tw-px-4 tw-py-3 tw-bg-gray-50 tw-text-sm tw-text-gray-500 tw-text-center">
            Showing {maxRows} of {trendsData.length} results
          </div>
        )}
      </div>
    )
  }

  // Handle legacy format with keys, titles, formatters structure
  return trendsData.map((d, di) => {
    const keys = d.keys.filter((v) => v)
    const limitedData = d.data.slice(0, maxRows)

    return (
      <div key={di} className="tw-mt-4 tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm">
        <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
          <thead className="tw-bg-gray-50">
            <tr>
              {keys.map((k) => (
                <th key={k} className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                  {d.titles[k]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
            {limitedData.map((r, ri) => (
              <tr key={ri} className="hover:tw-bg-gray-50">
                {keys.map((k) => (
                  <td key={k} className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900">
                    {d.formatters[k] ? d.formatters[k](r[k], 'USD') : r[k]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {d.data.length > maxRows && (
          <div className="tw-px-4 tw-py-3 tw-bg-gray-50 tw-text-sm tw-text-gray-500 tw-text-center">
            Showing {maxRows} of {d.data.length} results
          </div>
        )}
      </div>
    )
  })
}

// Render traffic basic reports table (sessions, visitors, carts, checkouts, orders)
const renderTrafficBasicTable = (data, reportType, maxRows, reportData) => {
  if (!data || !data.length) return null

  const trafficConfig = getTrafficConfig(reportType)
  const trafficReportKey = reportType.replace(/s$/, '')
  const trafficColumnConfigs = _.get(reportData, ['config', 'config', 'columns', trafficReportKey], {})

  const { titles = {}, formatters = {}, keys = [] } = trafficConfig

  // Limit data to maxRows
  const limitedData = data.slice(0, maxRows)

  // Get visible keys based on column config
  const visibleKeys = keys.filter(k => !_.get(trafficColumnConfigs, [k, 'invisible'], false))

  // Show first 8 columns or all if fewer
  const displayKeys = visibleKeys.slice(0, 8)

  if (displayKeys.length === 0) {
    return null
  }

  return (
    <div className="tw-mt-4 tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm">
      <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
        <thead className="tw-bg-gray-50">
          <tr>
            {displayKeys.map((key) => (
              <th
                key={key}
                className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-whitespace-nowrap"
              >
                {titles[key] || key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
          {limitedData.map((row, index) => (
            <tr key={index} className="hover:tw-bg-gray-50">
              {displayKeys.map((key) => {
                let content = row[key]
                if (formatters[key]) {
                  try {
                    content = formatters[key](row[key], row.currency || 'USD', row)
                  } catch (e) {
                    content = row[key] || '-'
                  }
                }
                return (
                  <td
                    key={key}
                    className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900 tw-whitespace-nowrap tw-max-w-xs tw-truncate"
                    title={typeof content === 'string' ? content : undefined}
                  >
                    {content || '-'}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length > maxRows && (
        <div className="tw-px-4 tw-py-3 tw-bg-gray-50 tw-text-sm tw-text-gray-500 tw-text-center">
          Showing {maxRows} of {data.length} results
        </div>
      )}
    </div>
  )
}

// Render traffic ads reports as metric cards/boxes
const renderTrafficAdsMetrics = (data, reportType, maxRows) => {
  if (!data || !data.length) return null

  // Ads metrics are displayed as cards with title and value
  const limitedData = data.slice(0, maxRows)

  return (
    <div className="tw-mt-4">
      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-4">
        {limitedData.map((metric, idx) => (
          <div
            key={`ads-metric-${idx}`}
            className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-p-4 tw-shadow-sm"
          >
            <div className="tw-text-sm tw-text-slate-500 tw-mb-1">{metric.title}</div>
            <div className="tw-text-2xl tw-font-semibold tw-text-slate-900">{metric.value}</div>
            {metric.channel && (
              <div className="tw-text-xs tw-text-slate-400 tw-mt-1">{metric.channel}</div>
            )}
          </div>
        ))}
      </div>
      {data.length > maxRows && (
        <div className="tw-mt-4 tw-text-sm tw-text-gray-500 tw-text-center">
          Showing {maxRows} of {data.length} metrics
        </div>
      )}
    </div>
  )
}

// Metric labels for display (matching TrafficTrendsReport)
const trafficMetricLabels = {
  pageview: 'Page Views',
  order: 'Orders',
  cart: 'Carts',
  checkout: 'Checkouts',
  sales_usd: 'Sales',
}

// Group by labels for display (matching TrafficTrendsReport)
const trafficGroupByLabels = {
  checkout_stage: 'Cart Status',
  country_code: 'Country',
  domain: 'Domain',
  utm_campaign: 'UTM Campaign',
  utm_medium: 'UTM Medium',
  utm_source: 'UTM Source',
  utm_term: 'UTM Term',
  traffic_type: 'Traffic Type',
  entrypoint: 'Landing Page',
}

// Metric to total key mapping
const metricToTotalKey = {
  pageview: 'total_pageviews',
  cart: 'total_carts',
  order: 'total_orders',
  checkout: 'total_checkouts',
  sales_usd: 'total_sales_usd',
}

// Generate a color from a string (for chart lines)
const stringToColor = (str) => {
  if (!str) return '#3b82f6'
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = hash % 360
  return `hsl(${hue}, 70%, 50%)`
}

// Traffic Trends Chart Component
const TrafficTrendsChart = ({ columns, chartLines, metric, currency = 'USD' }) => {
  const chartOptions = {
    chart: {
      height: 300,
    },
    title: {
      text: '',
    },
    xAxis: {
      categories: columns.map(c => c.header),
    },
    yAxis: {
      title: {
        text: '',
      },
      plotLines: [
        {
          value: 0,
          width: 1,
          color: '#808080',
        },
      ],
    },
    tooltip: {
      valueSuffix: '',
      formatter: function () {
        const value = metric === 'sales_usd'
          ? `${currency} $${this.y.toLocaleString()}`
          : this.y.toLocaleString()
        return `<span style="font-size: 10px; color: #64748b;">${this.x}</span><br />${this.series.name}: <b>${value}</b>`
      },
    },
    legend: {
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'top',
      borderWidth: 0,
      itemWidth: 200,
    },
    series: chartLines,
  }

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />
}

// Render traffic trends report (similar to TrafficTrendsReport component)
const renderTrafficTrendsTable = (data, maxRows, reportData) => {
  if (!data || !data.length) return null

  // Get metric and groupBy from report config (matching Reports.js logic)
  const trafficTrendsParams = _.get(reportData, ['config', 'params'], {})
  const trafficTrendsConfigData = _.get(reportData, ['config', 'config', 'trafficTrends'], {})
  const metric = trafficTrendsParams.metric || trafficTrendsConfigData.metric || 'pageview'
  const groupBy = trafficTrendsParams.groupBy || trafficTrendsConfigData.groupBy || ''
  const currency = reportData?.currency || 'USD'

  const totalKey = metricToTotalKey[metric] || 'total_pageviews'

  // Get unique time intervals sorted
  const uniqueTimeIntervals = _.chain(data)
    .uniqBy('date_trunc')
    .sortBy('date_trunc')
    .map(d => d.date_trunc)
    .value()

  // Generate column headers from time intervals
  const columns = uniqueTimeIntervals.map(dateStr => {
    const m = moment(dateStr)
    return {
      key: m.format('MMM-D').toLowerCase(),
      header: m.format('MMM D'),
      date: dateStr,
    }
  })

  // Group data by groupBy field (or treat as single group if no groupBy)
  const groupedData = groupBy
    ? _.groupBy(data, groupBy)
    : { 'Total': data }

  // Build rows and chart lines
  const rows = []
  const chartLines = []

  Object.keys(groupedData).forEach((groupKey) => {
    const groupItems = groupedData[groupKey]
    const color = stringToColor(groupKey)

    // Create row data with segment name and values for each column
    const row = {
      segment: groupKey,
    }

    // Create chart line data
    const chartData = []

    // Map data to columns
    columns.forEach(col => {
      const matchingItem = groupItems.find(item => {
        const itemDate = moment(item.date_trunc).format('MMM-D').toLowerCase()
        return itemDate === col.key
      })
      // Parse value as number (API may return strings)
      const rawValue = matchingItem ? matchingItem[totalKey] : 0
      const value = typeof rawValue === 'string' ? parseFloat(rawValue) || 0 : (rawValue || 0)
      row[col.key] = metric === 'sales_usd' ? `$${value.toLocaleString()}` : value.toLocaleString()
      chartData.push(value)
    })

    // Calculate total (parse values as numbers)
    let total = groupItems.reduce((sum, item) => {
      const rawValue = item[totalKey]
      const numValue = typeof rawValue === 'string' ? parseFloat(rawValue) || 0 : (rawValue || 0)
      return sum + numValue
    }, 0)

    row['total'] = metric === 'sales_usd'
      ? `$${total.toLocaleString()}`
      : total.toLocaleString()

    rows.push(row)
    chartLines.push({
      name: groupKey,
      data: chartData,
      color,
      marker: {
        fillColor: 'transparent',
        lineColor: 'transparent',
      },
    })
  })

  // Limit rows to maxRows
  const limitedRows = rows.slice(0, maxRows)

  // Build table keys and names
  const tableKeys = groupBy
    ? ['segment', ...columns.map(c => c.key), 'total']
    : columns.map(c => c.key).concat(['total'])

  const tableNames = groupBy
    ? {
        segment: trafficGroupByLabels[groupBy] || 'Segment',
        ...columns.reduce((acc, c) => ({ ...acc, [c.key]: c.header }), {}),
        total: 'Total',
      }
    : {
        ...columns.reduce((acc, c) => ({ ...acc, [c.key]: c.header }), {}),
        total: 'Total',
      }

  return (
    <div className="tw-mt-4">
      {/* Header showing metric and segment */}
      <div className="tw-mb-4 tw-text-sm tw-text-slate-600">
        <span className="tw-font-medium">Metric:</span> {trafficMetricLabels[metric] || metric}
        {groupBy && (
          <span className="tw-ml-4">
            <span className="tw-font-medium">Segment By:</span> {trafficGroupByLabels[groupBy] || groupBy}
          </span>
        )}
      </div>

      {/* Chart - only show if 50 or fewer segments */}
      {chartLines.length <= 50 && (
        <div className="tw-mb-6">
          <TrafficTrendsChart
            columns={columns}
            chartLines={chartLines}
            metric={metric}
            currency={currency}
          />
        </div>
      )}

      {chartLines.length > 50 && (
        <div className="tw-mb-4 tw-text-sm tw-text-amber-600 tw-bg-amber-50 tw-p-3 tw-rounded">
          Chart hidden due to large number of segments ({chartLines.length}). Showing table only.
        </div>
      )}

      {/* Table */}
      <div className="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm">
        <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
          <thead className="tw-bg-gray-50">
            <tr>
              {tableKeys.map((key) => (
                <th
                  key={key}
                  className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-whitespace-nowrap"
                >
                  {tableNames[key] || key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
            {limitedRows.map((row, index) => (
              <tr key={index} className="hover:tw-bg-gray-50">
                {tableKeys.map((key) => (
                  <td
                    key={key}
                    className={`tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900 tw-whitespace-nowrap ${
                      key === 'total' ? 'tw-font-medium tw-bg-gray-50' : ''
                    }`}
                  >
                    {row[key] || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > maxRows && (
          <div className="tw-px-4 tw-py-3 tw-bg-gray-50 tw-text-sm tw-text-gray-500 tw-text-center">
            Showing {maxRows} of {rows.length} segments
          </div>
        )}
      </div>
    </div>
  )
}

// Render inventory reports table
const renderInventoryTable = (data, reportType, maxRows, reportData) => {
  if (!data || !data.length) return null

  // Get the correct config based on report type
  const configMap = {
    inventoryProducts: inventoryProductsConfig,
    inventoryVariants: inventoryVariantsConfig,
    inventoryBrands: inventoryBrandsConfig,
  }

  const config = configMap[reportType]
  if (!config) return null

  const { titles = {}, formatters = {}, keys = [] } = config

  // Limit data to maxRows
  const limitedData = data.slice(0, maxRows)

  // Get visible keys - filter out image keys for now since they need special handling
  const displayKeys = keys.filter(k => k !== 'product_image').slice(0, 10)

  // Check if we have an image column
  const hasImage = keys.includes('product_image')

  // Numeric keys for right-alignment
  const numericKeys = ['quantity', 'inventory_purchased', 'quantity_sold', 'average_inventory_quantity',
    'sell_through_rate', 'inventory_turn', 'inventory_value', 'inventory_cost', 'total_sales', 'total_profit',
    'sales_velocity', 'days_of_stock', 'product_count', 'total_quantity', 'total_inventory_value',
    'total_inventory_cost', 'low_stock_count', 'out_of_stock_count', 'average_price']

  return (
    <div className="tw-mt-4 tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm">
      <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
        <thead className="tw-bg-gray-100">
          <tr>
            {hasImage && (
              <th className="tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-w-14">
                
              </th>
            )}
            {displayKeys.map((key) => (
              <th
                key={key}
                className={`tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap ${
                  numericKeys.includes(key) ? 'tw-text-right' : 'tw-text-left'
                }`}
              >
                {titles[key] || key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="tw-divide-y tw-divide-gray-100">
          {limitedData.map((row, index) => (
            <tr
              key={index}
              className={`hover:tw-bg-blue-50 tw-transition-colors ${index % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50/50'}`}
            >
              {hasImage && (
                <td className="tw-px-4 tw-py-3 tw-text-center tw-w-14">
                  <img
                    src={row.product_image || '/assets/images/image_placeholder.png'}
                    alt={row.product_name || 'Product'}
                    className="tw-w-10 tw-h-10 tw-object-cover tw-rounded-md tw-border tw-border-gray-200"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      // Prevent infinite loop by checking if already set to placeholder
                      if (!target.src.includes('image_placeholder.png')) {
                        target.src = '/assets/images/image_placeholder.png'
                      }
                    }}
                  />
                </td>
              )}
              {displayKeys.map((key) => {
                let content = row[key]

                // Apply formatter if available
                if (formatters[key]) {
                  try {
                    content = formatters[key](row[key], row.currency || 'USD', row)
                  } catch (e) {
                    content = row[key] ?? '-'
                  }
                }

                // Handle low_stock boolean
                if (key === 'low_stock') {
                  content = row[key] ? (
                    <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-red-100 tw-text-red-700">
                      Low Stock
                    </span>
                  ) : (
                    <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-green-100 tw-text-green-700">
                      In Stock
                    </span>
                  )
                }

                const isNumeric = numericKeys.includes(key)

                return (
                  <td
                    key={key}
                    className={`tw-px-4 tw-py-3 tw-text-sm ${
                      isNumeric
                        ? 'tw-text-right tw-font-medium tw-text-gray-900 tw-whitespace-nowrap'
                        : 'tw-text-gray-700 tw-whitespace-nowrap tw-max-w-xs tw-truncate'
                    }`}
                    title={typeof content === 'string' ? content : undefined}
                  >
                    {content ?? '-'}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {data.length > maxRows && (
        <div className="tw-px-4 tw-py-2 tw-bg-gray-100 tw-text-xs tw-text-gray-500 tw-text-center tw-border-t tw-border-gray-200">
          Showing {maxRows} of {data.length} results
        </div>
      )}
    </div>
  )
}

// Render default table similar to ListTable for regular report types
export interface ReportTableOptions {
  /**
   * Follow the saved report's column settings (visible columns, order, group-by fields first, form-field titles)
   * exactly like the Reports page does, instead of the chat's "first 8 columns that have data" summary view.
   */
  respectColumnConfig?: boolean
}

/** Visible columns, in the saved order, plus titles, mirroring Reports.tsx's list table. */
const savedColumnLayout = (reportType: string, reportData: any, baseKeys: string[], baseTitles: Record<string, string>) => {
  const reportKey = reportType.replace(/s$/, '')
  const saved = _.get(reportData, ['config', 'config', 'columns', reportKey], {})
  const columnConfigs: Record<string, any> = { ..._.get(initialColumnConfigs, [reportKey], {}), ...saved }

  // Pivot calculations are extra columns
  const customConfigs: Array<{ field_name: string }> = _.get(reportData, ['custom_config'], []) || []
  const calcKeys = Array.isArray(customConfigs) ? customConfigs.map((c) => c.field_name) : []
  calcKeys.forEach((k) => { columnConfigs[k] = { sort: null, order: 58, invisible: false } })

  let keys = _.uniq([...baseKeys, ...calcKeys]).filter((k) => !_.get(columnConfigs, [k, 'invisible'], false))
  keys = keys.sort(sortReportColKeys(columnConfigs))

  // Group-by fields (pivot / product timeline) come first, with the field name in the title
  const params = _.get(reportData, ['config', 'params'], {})
  const appliedFields: string[] = (params.fields || [params.field]).filter(Boolean)
  const appliedValues: string[] = params.field_values || [params.field_value]
  if (appliedFields.length) keys = [...appliedFields, ...keys.filter((k) => !appliedFields.includes(k))]
  const titles: Record<string, string> = { ...baseTitles }
  calcKeys.forEach((k) => { titles[k] = k })
  appliedFields.forEach((f, i) => {
    const title = pivotColumnTitle(f, appliedValues[i], baseTitles[f])
    if (title) titles[f] = title
  })
  return { keys, titles }
}

const renderDefaultTable = (data, reportType, maxRows, reportData = null, options: ReportTableOptions = {}) => {
  if (!data.length) return null

  // Get configuration for this report type
  const config = configSets[`${reportType}Config`]
  if (!config) {
    console.warn(`No configuration found for report type: ${reportType}`)
    return null
  }

  const { titles: configTitles = {}, formatters = {}, keys: configKeys = [], imageFields = [] } = config
  let titles = configTitles
  let keys = configKeys
  if (options.respectColumnConfig) ({ keys, titles } = savedColumnLayout(reportType, reportData, configKeys, configTitles))

  // Limit data to maxRows for performance
  const limitedData = data.slice(0, maxRows)

  // Determine which columns to show based on available data and config
  const availableKeys = options.respectColumnConfig ? keys : keys.filter(key => {
    // Check if any data row has this key with a meaningful value
    return limitedData.some(row =>
      row.hasOwnProperty(key) &&
      row[key] !== null &&
      row[key] !== undefined &&
      row[key] !== ''
    )
  })

  // Prioritize image fields at the start, then show first 8 columns
  const imageKeysToShow = availableKeys.filter(k => imageFields.includes(k))
  const nonImageKeys = availableKeys.filter(k => !imageFields.includes(k))
  const displayKeys = options.respectColumnConfig
    ? [...imageKeysToShow, ...nonImageKeys]
    : [...imageKeysToShow, ...nonImageKeys.slice(0, 8 - imageKeysToShow.length)]

  if (displayKeys.length === 0) {
    return null
  }

  // Content wrapper function similar to ListTable
  const contentWrapper = (el, key) => {
    let content

    if (imageFields.includes(key)) {
      const imagePlaceholder = '/assets/images/image_placeholder.png'
      const imageUrl = el[key] || imagePlaceholder
      content = (
        <div className="tw-flex tw-items-center tw-justify-center">
          <img
            src={imageUrl}
            alt={el.title || 'Product'}
            className="tw-w-10 tw-h-10 tw-object-cover tw-rounded-md tw-border tw-border-gray-200"
            onError={(e) => {
              const target = e.target as HTMLImageElement
              // Prevent infinite loop by checking if already set to placeholder
              if (!target.src.includes('image_placeholder')) {
                target.src = imagePlaceholder
              }
            }}
          />
        </div>
      )
    } else if (formatters[key]) {
      try {
        content = formatters[key](el[key], el.currency || el.currency_id || 'USD', el)
      } catch (e) {
        content = el[key] || '-'
      }
    } else {
      content = (el[key] || '').toString() || '-'
    }

    return content
  }

  // Check if we have numeric columns for right-alignment
  const numericKeys = ['sales', 'total_sales', 'net_sales', 'gross_sales', 'profit', 'total_orders',
    'items_sold', 'total_items_sold', 'customers', 'total_customers', 'avg_order', 'avg_price',
    'total_tax', 'total_shipping', 'total_discount', 'gross_margin', 'cost_of_goods_sold']

  return (
    <div className="tw-mt-4 tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm">
      <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
        <thead className="tw-bg-gray-100">
          <tr>
            {displayKeys.map((key) => (
              <th
                key={key}
                className={`tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap ${
                  imageFields.includes(key) ? 'tw-text-center tw-w-14' :
                  numericKeys.some(nk => key.includes(nk)) ? 'tw-text-right' : 'tw-text-left'
                }`}
              >
                {imageFields.includes(key) ? '' : (titles[key] || key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="tw-divide-y tw-divide-gray-100">
          {limitedData.map((row, index) => (
            <tr
              key={index}
              className={`hover:tw-bg-blue-50 tw-transition-colors ${index % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50/50'}`}
            >
              {displayKeys.map((key) => {
                const content = contentWrapper(row, key)
                const displayValue = typeof content === 'string' ? content : String(content)
                const isNumeric = numericKeys.some(nk => key.includes(nk))

                return (
                  <td
                    key={key}
                    className={`tw-px-4 tw-py-3 tw-text-sm ${
                      imageFields.includes(key)
                        ? 'tw-text-center tw-w-14'
                        : isNumeric
                          ? 'tw-text-right tw-font-medium tw-text-gray-900 tw-whitespace-nowrap'
                          : 'tw-text-gray-700 tw-whitespace-nowrap tw-max-w-xs tw-truncate'
                    }`}
                    title={imageFields.includes(key) ? undefined : displayValue}
                  >
                    {content}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {data.length > maxRows && (
        <div className="tw-px-4 tw-py-2 tw-bg-gray-100 tw-text-xs tw-text-gray-500 tw-text-center tw-border-t tw-border-gray-200">
          Showing {maxRows} of {data.length} results
        </div>
      )}
    </div>
  )
}

/** The table for a report (null when it has no rows), so callers can hide an empty tab. */
export const renderReportTable = (reportData: any = null, reportType: string | null = null, maxRows = 50, options: ReportTableOptions = {}) => {
  // For traffic reports, reportType will be "traffic" - check search_type for actual type
  const actualReportType = reportType === 'traffic'
    ? (reportData?.search_type || reportType)
    : reportType

    // Handle special report types with different table structures
    if (actualReportType === 'productTrends') {
      const tableData = reportData?.data?.[actualReportType] || []
      return renderProductTrendsTable(tableData, reportData, maxRows)
    }

    if (actualReportType === 'yearComparison') {
      return renderYearComparisonTable(reportData, maxRows)
    }

    if (actualReportType === 'trends') {
      const tableData = reportData?.data?.[actualReportType] || reportData?.data?.trends || []
      return renderTrendsTable(tableData, maxRows, reportData)
    }

    // Handle traffic basic reports (sessions, visitors, carts, checkouts, orders)
    // Traffic reports use data.docs instead of data.[reportType]
    if (trafficBasicReportTypes.includes(actualReportType)) {
      const trafficData = reportData?.data?.docs || []
      return renderTrafficBasicTable(trafficData, actualReportType, maxRows, reportData)
    }

    // Handle traffic ads reports (metric cards)
    if (trafficAdsReportTypes.includes(actualReportType)) {
      const adsData = reportData?.data?.docs || []
      return renderTrafficAdsMetrics(adsData, actualReportType, maxRows)
    }

    // Handle traffic trends report
    if (actualReportType === 'trafficTrends') {
      const trendsData = reportData?.data?.docs || []
      return renderTrafficTrendsTable(trendsData, maxRows, reportData)
    }

    // Handle inventory reports - check by report type or by data keys
    const dataObj = reportData?.data as Record<string, unknown> || {}
    const docsArray = dataObj?.docs as Array<Record<string, unknown>> || []
    const hasInventoryDocs = docsArray.length > 0 && (docsArray[0]?.quantity !== undefined || docsArray[0]?.inventory_value !== undefined)
    
    const isInventory = inventoryReportTypes.includes(actualReportType) ||
                        actualReportType?.startsWith('inventory') ||
                        actualReportType?.includes('inventory') ||
                        reportData?.data?.inventoryProducts ||
                        reportData?.data?.inventoryVariants ||
                        reportData?.data?.inventoryBrands ||
                        hasInventoryDocs
    
    if (isInventory) {
      // Determine the actual inventory type
      const inventoryType = actualReportType?.startsWith('inventory') ? actualReportType :
                            reportData?.data?.inventoryProducts ? 'inventoryProducts' :
                            reportData?.data?.inventoryVariants ? 'inventoryVariants' :
                            reportData?.data?.inventoryBrands ? 'inventoryBrands' : actualReportType
      
      // Primary location: reportData.data.docs (same as traffic reports)
      let inventoryData = reportData?.data?.docs || []
      
      // Fallback: try other possible locations
      if (!inventoryData || inventoryData.length === 0) {
        inventoryData = reportData?.data?.[inventoryType] || 
                        reportData?.data?.[actualReportType] ||
                        reportData?.data?.inventoryProducts ||
                        reportData?.data?.inventoryVariants ||
                        reportData?.data?.inventoryBrands ||
                        []
      }
      
      // If data is still empty, check if reportData.data itself is an array
      if ((!inventoryData || inventoryData.length === 0) && Array.isArray(reportData?.data) && reportData?.data[0]?.quantity !== undefined) {
        inventoryData = reportData.data
      }
      
      if (inventoryData && inventoryData.length > 0) {
        return renderInventoryTable(inventoryData, inventoryType, maxRows, reportData)
      }
    }

    // Extract data from reportData based on reportType for regular reports
    const tableData = reportData?.data?.[actualReportType] || []

    // Return null if no data or report type
    if (!tableData.length || !actualReportType) {
      return null
    }

    // Use default ListTable-style rendering for regular report types
    return renderDefaultTable(tableData, actualReportType, maxRows, reportData, options)
}

const ReportDataTable = React.memo(({
  reportType = null,
  maxRows = 50,
  reportData = null,
  respectColumnConfig = false,
}: { reportType?: string | null; maxRows?: number; reportData?: any; respectColumnConfig?: boolean }) => {
  // Memoize the rendered content based on report type
  const renderedTable = useMemo(
    () => renderReportTable(reportData, reportType, maxRows, { respectColumnConfig }),
    [reportType, reportData, maxRows, respectColumnConfig],
  )

  return renderedTable
})

export default ReportDataTable